// Reactの必要なフックとライブラリをインポート
import { useState, useCallback, useEffect } from 'react';
import WordCloud from 'react-d3-cloud'; // WordCloud用のライブラリ

// styles imports
import './WordCloudView.scss';

const WordCloudView = (props) => {
  // 現在クリックされた単語を保持するステート
  const [selectedWord, setSelectedWord] = useState(null);
  const [wordsTemp, setWordsTemp] = useState(null);
  const [maxFrequency, setMaxFrequency] = useState(null);
  const [minFrequency, setMinFrequency] = useState(null);
  const [wordsCount, setWordsCount] = useState(null);

  // valueが0～100の整数値とする
const getColor = (value) => {
  // COLOR_DICTのキーを数値配列に変換し昇順ソート
  const keys = Object.keys(props.colorDict).map(Number).sort((a, b) => a - b);
  
  // valueより大きいキーが出るまでループ、valueより大きいキーがあれば一つ前のキーの色を返す
  let matchedKey = keys[0];
  for (let i = 0; i < keys.length; i++) {
    if (value < keys[i]) {
      break;
    }
    matchedKey = keys[i];
  }
  
  return props.colorDict[matchedKey];
}

  useEffect(() => {
    if (props.onWordClick)
    {
      props.onWordClick(selectedWord)
    }
  }, [selectedWord]);

  // 単語がクリックされたときの処理（選択された単語をステートに保存）
  const onWordClick = useCallback((e, { text }) => {
    setSelectedWord(text)
  }, []);

  // 単語のフォントサイズを決定する関数
  const fontSize = useCallback((word) => {
    // 領域サイズと単語数から最大フォントサイズを決定
    const area = (props.width) * (props.height);
    // 単語数に応じて係数を調整（単語が少ないほど小さくする）
    const wordCountFactor = Math.min(1, wordsCount / 10); // 10単語以下の場合は係数を小さくする
    const adjustedArea = area * (0.5 + wordCountFactor * 0.5); // 係数を0.5〜1.0の範囲で調整

    // 最大フォントサイズを計算（領域に対して適切なサイズに制限）
    const maxFont = Math.min(
      Math.sqrt(adjustedArea / Math.max(wordsCount, 1)),
      Math.min(props.width, props.height) * 0.3 // 領域の30%を超えないように制限
    );
    const minFont = Math.max(12, maxFont * 0.3); // 最小サイズも調整

    // min-max正規化
    const normalized = (maxFrequency > minFrequency)
      ? (word.value - minFrequency) / (maxFrequency - minFrequency)
      : (word.value / maxFrequency);

    // フォントサイズをminFont〜maxFontで線形補間
    return minFont + (maxFont - minFont) * normalized;
  }, [maxFrequency, minFrequency, wordsCount, props.width, props.height]);

  // 単語の回転角度を決定する関数（0度または90度）
  // const rotate = useCallback((word) => ~~(Math.random() * 2) * 90, []); // ランダムに選択
  const rotate = useCallback((word) => 0, []);

  // 出現数に基づいて色（透明度）を設定
  const fill = useCallback((word) => {
    const frequency = ((word.value - minFrequency) / (maxFrequency - minFrequency)) * 100; // 最大値からの割合(%)を取得
    const colorHex = getColor(frequency)

    const [r, g, b] = colorHex.match(/\w\w/g).map((hex) => parseInt(hex, 16)); // RGB値を抽出
    return `rgba(${r}, ${g}, ${b}, ${1})`; // RGBA形式の色を返す
  }, [maxFrequency]);

  useEffect(() => {
    setWordsTemp(JSON.parse(JSON.stringify(props.words)))
    setMaxFrequency(props.words ? Math.max(...props.words.map((word) => word.value)) : 1)
    setMinFrequency(props.words ? Math.min(...props.words.map((word) => word.value)) : 0)
    setWordsCount(props.words ? props.words.length : 1)
  }, [props.words]);

  if (!wordsTemp)
  {
    return  <div className={"wordcloud-view " + (props.className ? props.className : "")}> </div>
  }

  return (
    <div className={"wordcloud-view " + (props.className ? props.className : "")}>
      <div className='wordcloud-box'>
        {/* WordCloudコンポーネントの描画 */}
        <WordCloud
          data={wordsTemp}          // データを渡す
          width={props.width}
          height={props.height}
          font="Noto Sans JP"               // フォント設定
          fontWeight="500"          // フォントの太さを設定
          fontSize={fontSize}        // フォントサイズの設定関数
          rotate={rotate}            // 回転角度の設定関数
          padding={1.5}                // 単語間の余白
          spiral="archimedean"
          random={Math.random}       // ランダム配置用関数
          onWordClick={onWordClick}  // クリック時の処理
          fill={fill}
        />
      </div>
    </div>
  );
}

// アプリケーションをエクスポート
export default WordCloudView;
